import { useState, useCallback, useMemo } from "react";
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// ── Types ────────────────────────────────────────────────────────────

export type PeriodType = "day" | "week" | "month" | "quarter" | "year" | "custom";

export interface PeriodState {
  periodType: PeriodType;
  startDate: string;
  endDate: string;
}

interface PeriodSelectorProps {
  value: PeriodState;
  onChange: (next: PeriodState) => void;
}

// ── Date helpers ─────────────────────────────────────────────────────

function toISO(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function todayISO(): string {
  return toISO(new Date());
}

function clampEndToToday(bounds: { startDate: string; endDate: string }) {
  const today = todayISO();
  return bounds.endDate > today ? { ...bounds, endDate: today } : bounds;
}

/** ISO day-of-week: Monday=1 … Sunday=7 */
function isoWeekday(d: Date): number {
  return ((d.getDay() + 6) % 7) + 1;
}

/** Get Monday of the ISO week containing `d`. */
function weekStart(d: Date): Date {
  const result = new Date(d);
  result.setDate(result.getDate() - (isoWeekday(result) - 1));
  return result;
}

function weekEnd(d: Date): Date {
  const mon = weekStart(d);
  const sun = new Date(mon);
  sun.setDate(sun.getDate() + 6);
  return sun;
}

function monthStartDate(year: number, month: number): string {
  return toISO(new Date(year, month, 1));
}

function monthEndDate(year: number, month: number): string {
  return toISO(new Date(year, month + 1, 0));
}

function quarterStartMonth(q: number): number {
  return (q - 1) * 3;
}

function quarterEndMonth(q: number): number {
  return q * 3 - 1;
}

function getQuarter(month: number): number {
  return Math.floor(month / 3) + 1;
}

// ── Boundary calculators ─────────────────────────────────────────────

function computeDayBounds(dateStr: string): { startDate: string; endDate: string } {
  return { startDate: dateStr, endDate: dateStr };
}

function computeWeekBounds(dateStr: string): { startDate: string; endDate: string } {
  const d = new Date(dateStr + "T12:00:00");
  return { startDate: toISO(weekStart(d)), endDate: toISO(weekEnd(d)) };
}

function computeMonthBounds(year: number, month: number): { startDate: string; endDate: string } {
  return { startDate: monthStartDate(year, month), endDate: monthEndDate(year, month) };
}

function computeQuarterBounds(year: number, quarter: number): { startDate: string; endDate: string } {
  return {
    startDate: monthStartDate(year, quarterStartMonth(quarter)),
    endDate: monthEndDate(year, quarterEndMonth(quarter)),
  };
}

function computeYearBounds(year: number): { startDate: string; endDate: string } {
  return { startDate: `${year}-01-01`, endDate: `${year}-12-31` };
}

// ── Default period state (Month = current month) ────────────────────

export function defaultPeriodState(): PeriodState {
  const now = new Date();
  const bounds = computeMonthBounds(now.getFullYear(), now.getMonth());
  return { periodType: "month", ...clampEndToToday(bounds) };
}

// ── Period type buttons ──────────────────────────────────────────────

const PERIOD_TYPES: { value: PeriodType; label: string }[] = [
  { value: "day", label: "Day" },
  { value: "week", label: "Week" },
  { value: "month", label: "Month" },
  { value: "quarter", label: "Quarter" },
  { value: "year", label: "Year" },
  { value: "custom", label: "Custom" },
];

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const QUARTER_LABELS = ["Q1 (Jan–Mar)", "Q2 (Apr–Jun)", "Q3 (Jul–Sep)", "Q4 (Oct–Dec)"];

// ── Sub-controls ────────────────────────────────────────────────────

function DayControl({ value, onChange }: PeriodSelectorProps) {
  const handleChange = useCallback(
    (dateStr: string) => {
      if (!dateStr) return;
      onChange({ periodType: "day", ...computeDayBounds(dateStr) });
    },
    [onChange]
  );

  return (
    <Input
      type="date"
      value={value.startDate}
      onChange={(e) => handleChange(e.target.value)}
      max={todayISO()}
      className="w-40"
    />
  );
}

function WeekControl({ value, onChange }: PeriodSelectorProps) {
  const handlePrev = useCallback(() => {
    const d = new Date(value.startDate + "T12:00:00");
    d.setDate(d.getDate() - 7);
    onChange({ periodType: "week", ...computeWeekBounds(toISO(d)) });
  }, [value.startDate, onChange]);

  const handleNext = useCallback(() => {
    const d = new Date(value.startDate + "T12:00:00");
    d.setDate(d.getDate() + 7);
    const next = computeWeekBounds(toISO(d));
    if (next.startDate > todayISO()) return;
    onChange({ periodType: "week", ...next });
  }, [value.startDate, onChange]);

  const label = `${value.startDate} – ${value.endDate}`;
  const nextDisabled = new Date(value.endDate + "T12:00:00") >= new Date(todayISO() + "T12:00:00");

  return (
    <div className="flex items-center gap-1">
      <Button variant="ghost" size="icon" onClick={handlePrev} aria-label="Previous week">
        <ChevronLeft className="h-4 w-4" />
      </Button>
      <span className="text-sm tabular-nums min-w-[180px] text-center">{label}</span>
      <Button
        variant="ghost"
        size="icon"
        onClick={handleNext}
        disabled={nextDisabled}
        aria-label="Next week"
      >
        <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  );
}

function MonthControl({ value, onChange }: PeriodSelectorProps) {
  const d = new Date(value.startDate + "T12:00:00");
  const year = d.getFullYear();
  const month = d.getMonth();

  const handleMonthChange = useCallback(
    (newMonth: string) => {
      onChange({ periodType: "month", ...computeMonthBounds(year, Number(newMonth)) });
    },
    [year, onChange]
  );

  const handleYearChange = useCallback(
    (delta: number) => {
      onChange({ periodType: "month", ...computeMonthBounds(year + delta, month) });
    },
    [year, month, onChange]
  );

  const now = new Date();

  return (
    <div className="flex items-center gap-1">
      <Button variant="ghost" size="icon" onClick={() => handleYearChange(-1)} aria-label="Previous year">
        <ChevronLeft className="h-4 w-4" />
      </Button>
      <span className="text-sm tabular-nums w-12 text-center">{year}</span>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => handleYearChange(1)}
        disabled={year >= now.getFullYear()}
        aria-label="Next year"
      >
        <ChevronRight className="h-4 w-4" />
      </Button>
      <Select value={String(month)} onValueChange={handleMonthChange}>
        <SelectTrigger className="w-[140px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {MONTH_NAMES.map((name, i) => {
            const disabled = year === now.getFullYear() && i > now.getMonth();
            return (
              <SelectItem key={i} value={String(i)} disabled={disabled}>
                {name}
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
    </div>
  );
}

function QuarterControl({ value, onChange }: PeriodSelectorProps) {
  const d = new Date(value.startDate + "T12:00:00");
  const year = d.getFullYear();
  const quarter = getQuarter(d.getMonth());

  const handleQuarterChange = useCallback(
    (q: string) => {
      onChange({ periodType: "quarter", ...computeQuarterBounds(year, Number(q)) });
    },
    [year, onChange]
  );

  const handleYearChange = useCallback(
    (delta: number) => {
      onChange({ periodType: "quarter", ...computeQuarterBounds(year + delta, quarter) });
    },
    [year, quarter, onChange]
  );

  const now = new Date();
  const currentQ = getQuarter(now.getMonth());

  return (
    <div className="flex items-center gap-1">
      <Button variant="ghost" size="icon" onClick={() => handleYearChange(-1)} aria-label="Previous year">
        <ChevronLeft className="h-4 w-4" />
      </Button>
      <span className="text-sm tabular-nums w-12 text-center">{year}</span>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => handleYearChange(1)}
        disabled={year >= now.getFullYear()}
        aria-label="Next year"
      >
        <ChevronRight className="h-4 w-4" />
      </Button>
      <Select value={String(quarter)} onValueChange={handleQuarterChange}>
        <SelectTrigger className="w-[160px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {QUARTER_LABELS.map((label, i) => {
            const q = i + 1;
            const disabled = year === now.getFullYear() && q > currentQ;
            return (
              <SelectItem key={q} value={String(q)} disabled={disabled}>
                {label}
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
    </div>
  );
}

function YearControl({ value, onChange }: PeriodSelectorProps) {
  const year = new Date(value.startDate + "T12:00:00").getFullYear();

  const handleChange = useCallback(
    (delta: number) => {
      onChange({ periodType: "year", ...computeYearBounds(year + delta) });
    },
    [year, onChange]
  );

  const now = new Date();
  return (
    <div className="flex items-center gap-1">
      <Button variant="ghost" size="icon" onClick={() => handleChange(-1)} aria-label="Previous year">
        <ChevronLeft className="h-4 w-4" />
      </Button>
      <span className="text-sm tabular-nums w-12 text-center font-medium">{year}</span>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => handleChange(1)}
        disabled={year >= now.getFullYear()}
        aria-label="Next year"
      >
        <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  );
}

function CustomControl({ value, onChange }: PeriodSelectorProps) {
  const [localStart, setLocalStart] = useState(value.startDate);
  const [localEnd, setLocalEnd] = useState(value.endDate);

  const apply = useCallback(() => {
    if (localStart && localEnd && localStart <= localEnd) {
      onChange({ periodType: "custom", startDate: localStart, endDate: localEnd });
    }
  }, [localStart, localEnd, onChange]);

  const isValid = localStart && localEnd && localStart <= localEnd;
  const isDirty = localStart !== value.startDate || localEnd !== value.endDate;

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <Input
        type="date"
        value={localStart}
        onChange={(e) => setLocalStart(e.target.value)}
        max={todayISO()}
        className="w-40"
        aria-label="Start date"
      />
      <span className="text-xs text-muted-foreground">to</span>
      <Input
        type="date"
        value={localEnd}
        onChange={(e) => setLocalEnd(e.target.value)}
        max={todayISO()}
        className="w-40"
        aria-label="End date"
      />
      {isDirty && (
        <Button size="sm" onClick={apply} disabled={!isValid}>
          Apply
        </Button>
      )}
      {!isValid && localStart && localEnd && (
        <span className="text-xs text-destructive">Start must be ≤ end</span>
      )}
    </div>
  );
}

// ── Main component ──────────────────────────────────────────────────

export function PeriodSelector({ value, onChange }: PeriodSelectorProps) {
  const handleTypeChange = useCallback(
    (type: PeriodType) => {
      if (type === value.periodType) return;
      const now = new Date();
      switch (type) {
        case "day":
          onChange({ periodType: "day", ...computeDayBounds(todayISO()) });
          break;
        case "week":
          onChange({ periodType: "week", ...clampEndToToday(computeWeekBounds(todayISO())) });
          break;
        case "month":
          onChange({ periodType: "month", ...clampEndToToday(computeMonthBounds(now.getFullYear(), now.getMonth())) });
          break;
        case "quarter":
          onChange({
            periodType: "quarter",
            ...clampEndToToday(computeQuarterBounds(now.getFullYear(), getQuarter(now.getMonth()))),
          });
          break;
        case "year":
          onChange({ periodType: "year", ...clampEndToToday(computeYearBounds(now.getFullYear())) });
          break;
        case "custom":
          onChange({ periodType: "custom", startDate: value.startDate, endDate: value.endDate });
          break;
      }
    },
    [value, onChange]
  );

  const SubControl = useMemo(() => {
    switch (value.periodType) {
      case "day":
        return DayControl;
      case "week":
        return WeekControl;
      case "month":
        return MonthControl;
      case "quarter":
        return QuarterControl;
      case "year":
        return YearControl;
      case "custom":
        return CustomControl;
    }
  }, [value.periodType]);

  return (
    <div className="flex items-center gap-3 flex-wrap">
      <CalendarDays className="h-4 w-4 text-muted-foreground hidden sm:block" />

      {/* Period type segmented control */}
      <div
        className="inline-flex h-9 items-center rounded-lg bg-muted p-0.5 text-muted-foreground"
        role="group"
        aria-label="Period type"
      >
        {PERIOD_TYPES.map((pt) => (
          <button
            key={pt.value}
            type="button"
            onClick={() => handleTypeChange(pt.value)}
            aria-pressed={value.periodType === pt.value}
            className={cn(
              "inline-flex items-center justify-center rounded-md px-2.5 py-1 text-xs font-medium transition-all",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              value.periodType === pt.value
                ? "bg-background text-foreground shadow-sm"
                : "hover:text-foreground"
            )}
          >
            {pt.label}
          </button>
        ))}
      </div>

      {/* Period-specific sub-control */}
      <SubControl value={value} onChange={onChange} />
    </div>
  );
}
